// Vendor Libraries
import uuid4 from 'uuid-random';
import { PAGE_SIZE_MAX, PAGE_WIDTH_MAX, PROPOSAL_MODULES, TEARSHEET_PAGE_SIZE_CONVERSION_FACTOR, TRANSITION_ANALYSIS_MODULES, } from '../constants';
export const getPageNum = (moduleOrPageElement) => {
    return parseInt(moduleOrPageElement.dataset.page, 10);
};
export const getEmptyModuleCoordinates = (validModules, size, width) => {
    const validModule = validModules.find((moduleInfo) => moduleInfo.pageWidth === width && moduleInfo.pageSize === size);
    if (validModule) {
        return { x: validModule.x, y: validModule.y };
    }
    return null;
};
export const getPageNumFromPageId = (pageId) => {
    return parseInt(pageId.replace('pdf-page-', '').split('-')[0], 10);
};
export const getModuleSize = (moduleElement) => {
    return parseInt(moduleElement.dataset.size, 10);
};
export const getCustomizableFieldValues = (fields) => {
    return Object.keys(fields).reduce((acc, fieldName) => {
        acc[fieldName] = fields[fieldName].value;
        return acc;
    }, {});
};
export const convertModulesToSaveState = (modules) => {
    return modules.map((page) => {
        return page.map((module) => {
            return {
                ...module,
                // Ignore module field metadata and reduce customizable fields to their values
                customizable_fields: getCustomizableFieldValues(module.customizable_fields),
            };
        });
    });
};
export const getFiltersFromAllowedModuleSizes = (allowedModuleSizes, allModuleSizes) => {
    const all = allowedModuleSizes.length === allModuleSizes.length;
    let filters = {
        all,
    };
    allModuleSizes.forEach((moduleSize) => {
        filters = {
            ...filters,
            [moduleSize]: false,
        };
    });
    if (!all) {
        allowedModuleSizes.forEach((moduleSize) => {
            filters[moduleSize] = true;
        });
    }
    return filters;
};
/**
 * Checks if the space at the given coordinates, size and width is empty on a given page.
 * @param page The page to check for empty space.
 * @param x The x-coordinate of the space to check.
 * @param y The y-coordinate of the space to check.
 * @param size The size of the module to check.
 * @param width The width of the module to check.
 *
 * @return {boolean} Returns true if the space is empty, false otherwise.
 */
export const isSpaceEmpty = (page, x, y, size, width) => {
    return page.every((module) => {
        if (module.x_coordinate === undefined || module.y_coordinate === undefined)
            return false;
        const xOverlap = x < module.x_coordinate + module.module_width && module.x_coordinate < x + width;
        const yOverlap = y < module.y_coordinate + module.module_size / TEARSHEET_PAGE_SIZE_CONVERSION_FACTOR &&
            module.y_coordinate < y + size / TEARSHEET_PAGE_SIZE_CONVERSION_FACTOR;
        return !(xOverlap && yOverlap);
    });
};
/**
 * Finds an empty space in a report to place a module of a given size and width, starting at the top of the report.
 * If `selectedEmptyModulePosition` is provided, it'll start searching from that position.
 *
 * @param {number} modules - The report modules.
 * @param {number} size - The size of the module to place.
 * @param {number} width - The width of the module to place.
 * @param {Object|null} selectedEmptyModulePosition - The position of the selected empty module to start searching from.
 *
 * @return {Object|null} Returns the coordinates of the empty space found, or null if no space is available.
 */
export const findEmptySpace = (modules, size, width, selectedEmptyModulePosition = null) => {
    const pageHeight = PAGE_SIZE_MAX / TEARSHEET_PAGE_SIZE_CONVERSION_FACTOR;
    const moduleSizeInCoordinateGrid = size / TEARSHEET_PAGE_SIZE_CONVERSION_FACTOR;
    const startPage = selectedEmptyModulePosition?.page ?? 0;
    const startX = selectedEmptyModulePosition?.x ?? 0;
    const startY = selectedEmptyModulePosition?.y ?? 0;
    for (let pageIndex = startPage; pageIndex < modules.length; pageIndex += 1) {
        const page = modules[pageIndex];
        for (let y = startY; y + moduleSizeInCoordinateGrid <= pageHeight; y += 1) {
            for (let x = startX; x + width <= PAGE_WIDTH_MAX; x += 1) {
                if (isSpaceEmpty(page, x, y, size, width)) {
                    return { page: pageIndex, x, y };
                }
            }
        }
    }
    return null;
};
export const addModuleToReport = (defaultModule, x, y) => {
    const module = {
        ...defaultModule,
        x_coordinate: x,
        y_coordinate: y,
    };
    if (PROPOSAL_MODULES.has(module.module_type) || TRANSITION_ANALYSIS_MODULES.has(module.module_type)) {
        // if we are inserting a proposal or transition analysis module we need to assign
        // a uuid in the case of
        // generation without saving
        module.step_uuid = uuid4();
    }
    return module;
};
